/**
 * Which calendar item carries each schedule data-tour anchor (pure + testable).
 *
 * FullCalendar owns the event DOM, so the page cannot tag "the first row" by index the way a list
 * does. Instead this picks an item id up front, and the event renderer tags whichever item matches.
 * "First" is the earliest start, which is where FullCalendar draws it in the month grid.
 */
import type { CalendarEventResponse } from "../../../../types/api";
import type { FcEventInput } from "./scheduleCalendarEvents";
import { eventNeedsSetup } from "./itinerarySetup";
import { allEventCoaches } from "./gameCoaches";

/**
 * Whether an event bar shows the org-wide "Unassigned" tag. Only tournaments carry it: recruit
 * visits and one-offs are personal by nature. The event renderer and the tour target share this.
 */
export function eventShowsUnassignedTag(raw: CalendarEventResponse): boolean {
  // Game-level coaches count: the itinerary wizard assigns game by game.
  return allEventCoaches(raw).length === 0 && raw.eventType === "Tournament";
}

export interface ScheduleTourTargets {
  /** The first calendar event (not a call: a call click leaves the schedule). */
  open: string | null;
  /** The first event showing the "Needs setup" badge. */
  needsSetup: string | null;
  /** The first event showing the "Unassigned" tag. */
  unassigned: string | null;
}

const byStart = (a: FcEventInput, b: FcEventInput): number =>
  a.start < b.start ? -1 : a.start > b.start ? 1 : 0;

export function scheduleTourTargets(items: readonly FcEventInput[]): ScheduleTourTargets {
  // Array.prototype.sort is stable, so same-start items keep their list order.
  const events = items.filter((item) => item.extendedProps.raw).sort(byStart);
  // The badges only render on a whole-event bar, never on a "My events" per-game item.
  const bars = events.filter((item) => !item.extendedProps.game);

  const first = (candidates: readonly FcEventInput[]) => candidates[0]?.id ?? null;

  return {
    open: first(events),
    needsSetup: first(bars.filter((item) => eventNeedsSetup(item.extendedProps.raw!))),
    unassigned: first(bars.filter((item) => eventShowsUnassignedTag(item.extendedProps.raw!))),
  };
}
